<template>
  <v-container fluid>
    <v-row>
      <v-col>
        <v-card>
          <v-toolbar :dark="$vuetify.theme.dark" color="primary">
            <v-toolbar-title>
              {{ title }}
            </v-toolbar-title>
          </v-toolbar>
          <v-card-text>
            <v-row>
              <v-col>
                <session-request-result-info
                    :process-comment="request.processComment"
                    :request-status="request.status" />
              </v-col>
            </v-row>
            <custom-stepper
                v-model="stepNumber"
                :steps-param-array="stepsProperties"
                :loading="isLoading"
                :disabled-forward-btn="!isUserLoaded"
                :success="success">
              <template v-slot:main-1>
                <v-row>
                  <v-col>
                    <v-card>
                      <v-card-text>
                        <session-public-offers-info
                            v-if="sessionType === SESSION_TYPES.PUBLIC_OFFERS"
                            ref="sessionInfo"
                            :session-info="currentSession || templateSession"
                            :lot="lot"
                            create
                            :validated="saveSessionInfo" />
                        <session-public-offers-divisible-volume-info
                            v-if="sessionType === SESSION_TYPES.PUBLIC_OFFERS_DIVISIBLE_VOLUME"
                            ref="sessionInfo"
                            :session-info="currentSession || templateSession"
                            :lot="lot"
                            create
                            :validated="saveSessionInfo" />
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </template>
              <template v-slot:main-2>
                <v-row>
                  <v-col>
                    <business-document-list
                        ref="requestFiles"
                        :request-files="requestFiles"
                        :document-types="requiredDocumentTypes"
                        :readonly="readonly"
                        :loading="isLoading"
                        :lot-request-info="lotRequestInfo"
                        @deleted="getLotRequestFiles"
                        @uploaded="attachFileToLotRequest" />
                  </v-col>
                </v-row>
              </template>
              <template v-slot:main-3>
                <v-row>
                  <v-col>
                    <v-card>
                      <v-expansion-panels multiple>
                        <v-expansion-panel v-test="{ panel: '' }">
                          <v-expansion-panel-header>
                            <div id="info">
                              <v-icon>
                                info
                              </v-icon>
                              {{ $t('Информация по повторной сессии') }}
                            </div>
                          </v-expansion-panel-header>
                          <v-expansion-panel-content eager>
                            <v-row class="text-left px-4">
                              <session-public-offers-info
                                  v-if="sessionType === SESSION_TYPES.PUBLIC_OFFERS"
                                  :session-info="currentSession"
                                  :lot="lot"
                                  readonly
                                  :validated="saveSessionInfo" />
                              <session-public-offers-divisible-volume-info
                                  v-if="sessionType === SESSION_TYPES.PUBLIC_OFFERS_DIVISIBLE_VOLUME"
                                  :session-info="currentSession"
                                  :lot="lot"
                                  readonly
                                  :validated="saveSessionInfo" />
                            </v-row>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                        <v-expansion-panel v-test="{ panel: '' }">
                          <v-expansion-panel-header>
                            <div id="contact_mail">
                              <v-icon>
                                contact_mail
                              </v-icon>
                              {{ $t('Документы по торговой процедуре') }}
                            </div>
                          </v-expansion-panel-header>
                          <v-expansion-panel-content eager>
                            <v-row class="text-left px-4">
                              <business-document-list-view
                                  :request-files="requestFiles"
                                  :document-types="requiredDocumentTypes"
                                  :loading="isLoading" />
                            </v-row>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </v-card>
                  </v-col>
                </v-row>
              </template>
              <template v-slot:buttons-3>
                <v-btn
                    v-if="canSendRequest"
                    id="sendBtn"
                    color="primary"
                    :disabled="!!validationMessages.length || isLoading"
                    @click="createSessionRequest">
                  {{ $t('Отправить') }}
                </v-btn>
              </template>
            </custom-stepper>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  import Vue from 'vue'
  import CustomStepper from '@/components/reused/ui/CustomStepper.vue'
  import BusinessDocumentList from '@/components/reused/files/BusinessDocumentList.vue'
  import BusinessDocumentListView from '@/components/reused/files/BusinessDocumentListView.vue'
  import SessionRequestResultInfo from './SessionRequestResultInfo.vue'
  import SessionPublicOffersInfo from '@/components/reused/sessions/SessionPublicOffersInfo.vue'
  import SessionPublicOffersDivisibleVolumeInfo from '@/components/reused/sessions/SessionPublicOffersDivisibleVolumeInfo.vue'
  import { LOT_TYPE_ENUM } from '@/constants/enums'
  import {
    LOT_REQUEST_TYPES,
    LOT_REQUEST_STATUSES,
    LOT_STATUSES,
    SESSION_STATUSES,
    SESSION_TYPES
  } from '@/constants/business'
  import { mapActions, mapState } from 'vuex'
  import {
    LOT_MANAGEMENT,
    LOT_POSITIONS_MANAGEMENT,
    LOT_SESSIONS_MANAGEMENT,
    LOT_REQUESTS_MANAGEMENT,
    BUSINESS_FILES,
    CURRENT_USER,
    CURRENT_ORGANIZATION
  } from '@/constants/store.js'

  export default {
    components: {
      CustomStepper,
      BusinessDocumentList,
      BusinessDocumentListView,
      SessionRequestResultInfo,
      SessionPublicOffersInfo,
      SessionPublicOffersDivisibleVolumeInfo
    },
    props: {
      lotType: {
        type: String,
        required: true
      },
      lotId: {
        type: String,
        required: true
      },
      sessionType: {
        type: String,
        required: true
      },
      sessionId: {
        type: String
      }
    },
    data () {
      return {
        LOT_TYPE_ENUM,
        SESSION_TYPES,
        requestType: LOT_REQUEST_TYPES.SESSION_REGISTRATION,
        stepNumber: 1,
        isLoading: false,
        success: false
      }
    },
    computed: {
      ...mapState(LOT_MANAGEMENT.NAMESPACE, {
        currentLot: state => (state[LOT_MANAGEMENT.STATE.CURRENT_LOT])
      }),
      ...mapState(LOT_POSITIONS_MANAGEMENT.NAMESPACE, {
        positions: state => (state[LOT_POSITIONS_MANAGEMENT.STATE.POSITIONS] || [])
      }),
      ...mapState(LOT_SESSIONS_MANAGEMENT.NAMESPACE, {
        sessions: state => (state[LOT_SESSIONS_MANAGEMENT.STATE.SESSIONS] || []),
        currentSession: state => (state[LOT_SESSIONS_MANAGEMENT.STATE.CURRENT_SESSION]),
        templateSession: state => (state[LOT_SESSIONS_MANAGEMENT.STATE.TEMPLATE_SESSION] || { })
      }),
      ...mapState(LOT_REQUESTS_MANAGEMENT.NAMESPACE, {
        lastRequest: state => (state[LOT_REQUESTS_MANAGEMENT.STATE.LAST] || {}),
        requestSession: state => (state[LOT_REQUESTS_MANAGEMENT.STATE.REQUEST_SESSION] || null)
      }),
      ...mapState(BUSINESS_FILES.NAMESPACE, {
        requestFiles: state => (state[BUSINESS_FILES.STATE.REQUEST_FILES] || []),
        requiredDocumentTypes: state => (state[BUSINESS_FILES.STATE.REQUIRED_DOCUMENT_TYPES] || [])
      }),
      ...mapState(CURRENT_USER.NAMESPACE, {
        currentUser: state => state
      }),
      isUserLoaded () {
        return !!this.currentUser.contactPerson
      },
      lotRequestInfo () {
        return {
          lotId: this.lot.lotId,
          lotType: this.lot.lotType,
          requestType: this.requestType,
          creatorId: this.lot.creatorId
        }
      },
      stepsProperties () {
        return [
          {
            stepNumber: 1,
            stepTitle: this.$t('Создание сессии'),
            stepTip: '',
            successText: '',
            toNextStep: this.validateAndSaveSessionInfo,
            toPrevStep: this.toPrevStep,
            cancel: this.exitFromWizard
          },
          {
            stepNumber: 2,
            stepTitle: this.$t('Добавление документов'),
            stepTip: '',
            successText: '',
            toNextStep: this.validateRequestFiles,
            toPrevStep: this.toPrevStep,
            cancel: this.exitFromWizard
          },
          {
            stepNumber: 3,
            stepTitle: this.$t('Регистрация торговой сессии'),
            stepTip: '',
            successText: this.$t('Запрос успешно отправлен на обработку.'),
            toNextStep: null,
            toPrevStep: this.toPrevStep,
            cancel: this.exitFromWizard
          }
        ]
      },
      request () {
        if (this.lastRequest?.sessionId && this.sessionId && this.lastRequest.sessionId === this.sessionId) {
          return this.lastRequest
        }
        return {}
      },
      readonly () {
        return !!this.currentSession?.sessionId && !this.canSendRequest
      },
      lastDraftSession () {
        return this.sessions.find(s => s.sessionStatus === SESSION_STATUSES.DRAFT)
      },
      canSendRequest () {
        const status = this.request.status

        return !status ||
          status === LOT_REQUEST_STATUSES.REJECTED
      },
      lot () {
        return this.currentLot || {
          lotId: this.lotId,
          lotType: this.lotType
        }
      },
      title () {
        return this.$t('Создание заявки на регистрацию повторной торговой сессии по торговой процедуре № ') + (this.lot.lotNumber || '')
      },
      validationMessages () {
        return []
      },
      positionsCount () {
        return this.positions.length
      }
    },
    watch: {
      lotId (newVal, oldVal) {
        const vm = this
        if (!newVal && oldVal) {
          vm.clearStore().then(() => {
            vm.stepNumber = 1
          })
        }
      }
    },
    created () {
      const vm = this
      vm.clearStore().then(() => {
        if (vm.lotId && vm.lotType) {
          vm.initialize()
        }
      })
    },
    beforeDestroy () {
      this.clearStore()
    },
    methods: {
      ...mapActions(LOT_MANAGEMENT.NAMESPACE, {
        getLotInfoAction: LOT_MANAGEMENT.ACTIONS.GET_LOT_INFO,
        updateLotInfoAction: LOT_MANAGEMENT.ACTIONS.UPDATE_LOT_INFO,
        createLotInfoAction: LOT_MANAGEMENT.ACTIONS.CREATE_LOT_INFO,
        clearLotInfoAction: LOT_MANAGEMENT.ACTIONS.CLEAR_LOT_INFO
      }),
      ...mapActions(LOT_REQUESTS_MANAGEMENT.NAMESPACE, {
        getLastLotRequestInfoAction: LOT_REQUESTS_MANAGEMENT.ACTIONS.GET_LAST_LOT_REQUEST_INFO,
        createLotRequestAction: LOT_REQUESTS_MANAGEMENT.ACTIONS.CREATE_LOT_REQUEST,
        getLotRequestInfoAction: LOT_REQUESTS_MANAGEMENT.ACTIONS.GET_LOT_REQUEST_INFO,
        createSessionRequestAction: LOT_REQUESTS_MANAGEMENT.ACTIONS.CREATE_SESSION_REQUEST,
        updateSessionRequestAction: LOT_REQUESTS_MANAGEMENT.ACTIONS.UPDATE_SESSION_REQUEST,
        getLastSessionRequestAction: LOT_REQUESTS_MANAGEMENT.ACTIONS.GET_LAST_SESSION_REQUEST,
        clearLastLotRequestInfoAction: LOT_REQUESTS_MANAGEMENT.ACTIONS.CLEAR_LAST_LOT_REQUEST_INFO
      }),
      ...mapActions(LOT_POSITIONS_MANAGEMENT.NAMESPACE, {
        getPositionsListAction: LOT_POSITIONS_MANAGEMENT.ACTIONS.GET_POSITIONS_LIST,
        clearPositionsListAction: LOT_POSITIONS_MANAGEMENT.ACTIONS.CLEAR_POSITIONS_LIST
      }),
      ...mapActions(LOT_SESSIONS_MANAGEMENT.NAMESPACE, {
        createSessionAction: LOT_SESSIONS_MANAGEMENT.ACTIONS.CREATE_SESSION,
        updateSessionAction: LOT_SESSIONS_MANAGEMENT.ACTIONS.UPDATE_SESSION,
        getSessionsListAction: LOT_SESSIONS_MANAGEMENT.ACTIONS.GET_SESSIONS_LIST,
        getSessionInfoAction: LOT_SESSIONS_MANAGEMENT.ACTIONS.GET_SESSION_INFO,
        clearSessionsListAction: LOT_SESSIONS_MANAGEMENT.ACTIONS.CLEAR_SESSIONS_LIST,
        getTemplateSessionInfoAction: LOT_SESSIONS_MANAGEMENT.ACTIONS.GET_TEMPLATE_SESSION_INFO
      }),
      ...mapActions(BUSINESS_FILES.NAMESPACE, {
        getLotRequestFilesAction: BUSINESS_FILES.ACTIONS.GET_LOT_REQUEST_FILES,
        getLotRequestRequiredDocumentTypesAction: BUSINESS_FILES.ACTIONS.GET_LOT_REQUEST_REQUIRED_DOCUMENT_TYPES,
        attachFileToLotRequestAction: BUSINESS_FILES.ACTIONS.ATTACH_FILE_TO_LOT_REQUEST,
        clearLotRequestFilesAction: BUSINESS_FILES.ACTIONS.CLEAR_LOT_REQUEST_FILES,
        clearLotRequestRequiredDocumentTypesAction: BUSINESS_FILES.ACTIONS.CLEAR_LOT_REQUEST_REQUIRED_DOCUMENT_TYPES
      }),
      ...mapActions(CURRENT_ORGANIZATION.NAMESPACE, {
        getCurrentOrganizationAction: CURRENT_ORGANIZATION.ACTIONS.GET_CURRENT_ORGANIZATION,
        clearCurrentOrganizationAction: CURRENT_ORGANIZATION.ACTIONS.CLEAR_CURRENT_ORGANIZATION
      }),
      clearStore: async function () {
        await Promise.all(
          [
            this.clearLotInfoAction(),
            this.clearLastLotRequestInfoAction(),
            this.clearLotRequestRequiredDocumentTypesAction(),
            this.clearLotRequestFilesAction(),
            this.clearPositionsListAction(),
            this.clearSessionsListAction(),
            this.clearCurrentOrganizationAction()
          ])
      },
      initialize: async function () {
        this.isLoading = true
        const model = {
          requestType: this.requestType,
          lotType: this.lot.lotType,
          lotId: this.lot.lotId
        }
        try {
          await Promise.all([
            this.getLotInfoAction(model),
            this.getLotRequestRequiredDocumentTypesAction(model),
            this.getLotRequestFiles(),
            this.getPositionsListAction(model),
            this.getSessionsListAction(model),
            this.getCurrentOrganizationAction(),
            this.getSessionTemplate()
          ])

          if (this.requestType === LOT_REQUEST_TYPES.SESSION_REGISTRATION && this.sessionId) {
            await this.getLastSessionRequestAction({
              lotType: this.lotType,
              lotId: this.lotId,
              sessionType: this.sessionType,
              sessionId: this.sessionId,
              requestType: LOT_REQUEST_TYPES.SESSION_REGISTRATION
            })
            await this.getSessionInfoAction({
              lotType: this.lotType,
              lotId: this.lotId,
              sessionType: this.sessionType,
              sessionId: this.sessionId
            })
          } else if (this.requestType !== LOT_REQUEST_TYPES.SESSION_REGISTRATION) {
            await this.getLastLotRequestInfoAction(model)
          }
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      attachFileToLotRequest: async function (file) {
        this.isLoading = true
        const model = {
          requestType: this.requestType,
          lotType: this.lot.lotType,
          lotId: this.lot.lotId
        }
        try {
          await this.attachFileToLotRequestAction({
            ...model,
            fileId: file.fileId
          })
          await this.getLotRequestFiles()
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      getLotRequestFiles: async function () {
        this.isLoading = true
        const model = {
          requestType: this.requestType,
          lotId: this.lot.lotId,
          lotType: this.lot.lotType
        }
        try {
          await this.getLotRequestFilesAction(model)
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      getSessionTemplate: async function () {
        try {
          this.isLoading = true
          await this.getTemplateSessionInfoAction({
            lotId: this.lot.lotId,
            lotType: this.lot.lotType,
            sessionType: this.sessionType
          })
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      validateRequestFiles: async function (step) {
        try {
          if (this.readonly) {
            this.toNextStep(step)
            return
          }
          // проверяем данные документов
          await this.$refs.requestFiles.validate()
          //  на следующий шаг
          this.toNextStep(step)
        } catch (error) {
          this.throwIfNotValidationError(error)
          // если ошибка, дальше не переходим
        }
      },
      validateAndSaveSessionInfo: async function (step) {
        try {
          if (this.readonly) {
            this.toNextStep(step)
            return
          }

          await this.$refs.sessionInfo.validate()

          // после изменений получаем полную информацию по торговой сессии
          if (!this.sessionId && !!this.lastDraftSession?.sessionId) {
            await this.replaceCurrentRoute(`${this.urlRootPath}/lot/${this.lot.lotType}/${this.lot.lotId}/sessionWizard/${this.sessionType}/${this.lastDraftSession.sessionId}`)
            // инициализируем
            this.$nextTick(async () => {
              await this.initialize()
              this.toNextStep(step)
            })
          } else {
            if (this.requestType === LOT_REQUEST_TYPES.SESSION_REGISTRATION) {
              await this.getLastSessionRequestAction({
                lotType: this.lotType,
                lotId: this.lotId,
                sessionType: this.sessionType,
                sessionId: this.sessionId,
                requestType: LOT_REQUEST_TYPES.SESSION_REGISTRATION
              })
              await this.getSessionInfoAction({
                lotType: this.lotType,
                lotId: this.lotId,
                sessionType: this.sessionType,
                sessionId: this.sessionId
              })
            } else {
              await this.getLastLotRequestInfoAction({
                requestType: this.requestType,
                lotId: this.lot.lotId,
                lotType: this.lot.lotType
              })
            }

            this.toNextStep(step)
          }
        } catch (error) {
          this.throwIfUnknownError(error)
        // если ошибка, дальше не переходим
        }
      },
      saveSessionInfo: async function (session) {
        this.isLoading = true
        this.success = false

        var model = {
          lotType: this.lotType,
          lotId: this.lotId,
          sessionType: this.sessionType,
          session: session
        }

        try {
          if (this.sessionId) {
            model.sessionId = this.sessionId
            await this.updateSessionAction(model)
          } else {
            await this.createSessionAction(model)
          }

          model = {
            lotType: this.lotType,
            lotId: this.lotId
          }

          await this.getSessionsListAction(model)

          this.success = true
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          // для запрета перехода на следующий шаг пробрасываем ошибку
          throw error
        }
      },
      validateBeforeRequest: function () {
        const requestFiles = this.requestFiles || []
        if (!requestFiles.length) {
          throw new Vue.errorsTypes.ValidationError(this.$t('Нет прикрепленных файлов для отправки запроса.'))
        }
        if (this.validationMessages.length) {
          throw new Vue.errorsTypes.ValidationError()
        }
      },
      createSessionRequest: async function () {
        try {
          this.validateBeforeRequest()
          if (!this.requestFiles.length) {
            throw new Vue.errorsTypes.ValidationError(this.$t('Документы по торговой процедуре не добавлены.'))
          }
          this.isLoading = true
          this.success = false

          if (this.request?.requestId) {
            await this.updateSessionRequestAction({
              lotId: this.lot.lotId,
              requestType: this.requestType,
              lotType: this.lot.lotType,
              requestId: this.request.requestId,
              session: { sessionId: this.currentSession.sessionId, documents: this.requestFiles }
            })
          } else {
            await this.createSessionRequestAction({
              lotId: this.lot.lotId,
              requestType: this.requestType,
              lotType: this.lot.lotType,
              session: { sessionId: this.currentSession.sessionId, documents: this.requestFiles }
            })
          }

          if (this.requestType === LOT_REQUEST_TYPES.SESSION_REGISTRATION) {
            await Promise.all([
              await this.getLastSessionRequestAction({
                lotType: this.lotType,
                lotId: this.lotId,
                sessionType: this.sessionType,
                sessionId: this.sessionId,
                requestType: LOT_REQUEST_TYPES.SESSION_REGISTRATION
              }),
              await this.getSessionInfoAction({
                lotType: this.lotType,
                lotId: this.lotId,
                sessionType: this.sessionType,
                sessionId: this.sessionId
              }),
              this.getLotRequestFiles()
            ])
          } else {
            await Promise.all([
              this.getLastLotRequestInfoAction({
                lotId: this.lot.lotId,
                requestType: this.requestType,
                lotType: this.lot.lotType
              }),
              this.getSessionInfoAction({
                lotId: this.lot.lotId,
                lotType: this.lot.lotType,
                sessionType: this.currentSession.sessionType,
                sessionId: this.currentSession.sessionId
              }),
              this.getLotRequestFiles()
            ])
          }

          this.isLoading = false
          this.success = true
        } catch (error) {
          this.isLoading = false
          this.throwIfUnknownError(error)
        // для запрета перехода на следующий шаг пробрасываем ошибку
        }
      },
      getPositionsList: async function () {
        try {
          this.isLoading = true
          const model = {
            lotId: this.lot.lotId,
            lotType: this.lot.lotType
          }
          await Promise.all([
            // для получения флагов доступа
            this.getLotInfoAction(model),
            // для обновления списка
            this.getPositionsListAction(model)]
          )
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      getSessionsList: async function () {
        try {
          this.isLoading = true
          const model = {
            lotId: this.lot.lotId,
            lotType: this.lot.lotType
          }
          await Promise.all([
            // для получения флагов доступа
            this.getLotInfoAction(model),
            // для обновления списка
            this.getSessionsListAction(model),
            this.getPositionsListAction(model)
          ])
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      toNextStep: async function (step) {
        this.success = false
        this.stepNumber = step + 1
      },
      toPrevStep: function (step) {
        this.success = false
        this.stepNumber = step - 1
      },
      exitFromWizard: function () {
        this.goToRoute(`${this.urlRootPath}/lots/personal/${LOT_STATUSES.REGISTERED}`)
      }
    }
  }
</script>
